import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { FinansKategorileri, FinansKategoriForm } from '../types/finansKategorileri';
import { useTranslation } from 'react-i18next';

const FinansKategorileriPage: React.FC = () => {
    const { t } = useTranslation();
    const [kategoriler, setKategoriler] = useState<FinansKategorileri[]>([]);
    const [selectedKategori, setSelectedKategori] = useState<FinansKategorileri | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [activeTab, setActiveTab] = useState<number>(1); // 0: Gelir, 1: Gider
    const [formData, setFormData] = useState<FinansKategoriForm>({
        kategoriAdi: '',
        tur: 1
    });
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    useEffect(() => {
        fetchKategoriler();
    }, []);

    const fetchKategoriler = async () => {
        try {
            const response = await axios.get('/api/FinansKategorileri');
            setKategoriler(response.data);
        } catch (error) {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (selectedKategori) {
                await axios.put(`/api/FinansKategorileri/${selectedKategori.kategoriID}`, {
                    ...formData,
                    kategoriID: selectedKategori.kategoriID
                });
                toast.success(t('msg.islemBasarili'));
            } else {
                await axios.post('/api/FinansKategorileri', formData);
                toast.success(t('msg.islemBasarili'));
            }
            fetchKategoriler();
            handleCloseModal();
        } catch (error: any) {
            if (error.response?.data) {
                toast.error(error.response.data);
            } else {
                toast.error(t('msg.islemHatasi'));
            }
        }
    };

    const handleEdit = (kategori: FinansKategorileri) => {
        setSelectedKategori(kategori);
        setFormData({
            kategoriAdi: kategori.kategoriAdi,
            tur: kategori.tur === 'Gelir' ? 0 : 1
        });
        setShowModal(true);
    };

    const handleDelete = async (id: number) => {
        if (window.confirm(t('confirm.sil'))) {
            try {
                await axios.delete(`/api/FinansKategorileri/${id}`);
                toast.success(t('msg.islemBasarili'));
                fetchKategoriler();
            } catch (error) {
                toast.error('Kategori silinemedi');
            }
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedKategori(null);
        setFormData({
            kategoriAdi: '',
            tur: activeTab
        });
    };

    const handleShowModal = () => {
        setFormData({
            kategoriAdi: '',
            tur: activeTab
        });
        setShowModal(true);
    };

    const gelirKategorileri = kategoriler.filter(k => k.tur === 'Gelir');
    const giderKategorileri = kategoriler.filter(k => k.tur === 'Gider');

    const currentKategoriler = activeTab === 0 ? gelirKategorileri : giderKategorileri;

    const toggleSelectAll = () => {
        if (selectedIds.length === currentKategoriler.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(currentKategoriler.map(k => k.kategoriID));
        }
    };

    const toggleSelect = (id: number) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleBulkDelete = async () => {
        if (!window.confirm(`${selectedIds.length} kategori silinecek. Emin misiniz?`)) return;
        try {
            for (const id of selectedIds) {
                await axios.delete(`/api/FinansKategorileri/${id}`);
            }
            toast.success(`${selectedIds.length} kategori silindi`);
            setSelectedIds([]);
            fetchKategoriler();
        } catch {
            toast.error(t('msg.islemHatasi'));
        }
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

                .fk-page {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    padding: 28px 32px;
                    background: #f4f6fb;
                    min-height: 100vh;
                }

                /* ── Header ── */
                .fk-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 26px; flex-wrap: wrap; gap: 14px; }
                .fk-header-left { display: flex; flex-direction: column; gap: 3px; }
                .fk-title { font-size: 22px; font-weight: 700; color: #0f172a; letter-spacing: -0.4px; margin: 0; }
                .fk-subtitle { font-size: 13px; color: #94a3b8; }
                .fk-add-btn {
                    display: inline-flex; align-items: center; gap: 8px;
                    padding: 10px 20px; background: #cd6906; color: #fff;
                    border: none; border-radius: 8px;
                    font-size: 13.5px; font-weight: 600; font-family: inherit;
                    cursor: pointer; white-space: nowrap;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transition: background 0.15s, transform 0.12s, box-shadow 0.15s;
                }
                .fk-add-btn:hover { background: #b85d05; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(205,105,6,0.3); }
                .fk-add-btn svg { width: 15px; height: 15px; stroke: #fff; stroke-width: 2.5; fill: none; }

                /* ── Stat cards ── */
                .fk-stats { display: flex; gap: 16px; margin-bottom: 26px; flex-wrap: wrap; }
                .fk-stat {
                    background: #fff; border-radius: 12px; padding: 16px 20px;
                    display: flex; align-items: center; gap: 14px; flex: 1; min-width: 160px;
                    box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 2px 10px rgba(15,23,42,0.04);
                }
                .fk-stat-gelir { border-left: 4px solid #16a34a; background: #f0fdf4; }
                .fk-stat-gider { border-left: 4px solid #dc2626; background: #fef2f2; }
                .fk-stat-icon { width: 48px; height: 48px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 32px; }
                .fk-stat-icon svg { width: 32px; height: 32px; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
                .fk-stat-val { font-size: 24px; font-weight: 800; color: #0f172a; line-height: 1; }
                .fk-stat-lbl { font-size: 12px; color: #94a3b8; font-weight: 500; margin-top: 3px; }

                /* ── Card ── */
                .fk-card {
                    background: #fff; border-radius: 14px;
                    box-shadow: 0 1px 4px rgba(15,23,42,0.07), 0 4px 24px rgba(15,23,42,0.05);
                    overflow: hidden;
                }

                /* ── Tabs ── */
                .fk-tabs { padding: 20px 24px 0 24px; }
                .fk-tab-bar { display: inline-flex; background: #f1f5f9; border-radius: 10px; padding: 3px; gap: 3px; }
                .fk-tab {
                    padding: 8px 20px; border: none; border-radius: 8px;
                    font-size: 13px; font-weight: 600; font-family: inherit;
                    cursor: pointer; transition: all 0.15s;
                    background: #f3f4f6; color: #6b7280;
                }
                .fk-tab-active { background: #cd6906; color: #fff; box-shadow: none; }

                /* ── Table ── */
                .fk-table { width: 100%; border-collapse: collapse; }
                .fk-table thead tr { border-bottom: 1.5px solid #f1f5f9; }
                .fk-table thead th {
                    padding: 13px 20px; font-size: 11px; font-weight: 600;
                    letter-spacing: 0.5px;
                    color: #94a3b8; text-align: left; white-space: nowrap;
                }
                .fk-table thead th.fk-th-r { text-align: right; }
                .fk-table tbody tr { border-bottom: 1px solid #f8fafc; transition: background 0.13s; }
                .fk-table tbody tr:last-child { border-bottom: none; }
                .fk-table tbody tr:hover { background: #fef9f4; }
                .fk-table tbody td { padding: 15px 20px; font-size: 14px; color: #1e293b; vertical-align: middle; }

                .fk-cell-id { font-size: 12px; color: #94a3b8; font-weight: 500; width: 54px; }
                .fk-cell-name { font-weight: 600; color: #0f172a; font-size: 14px; }

                /* Pill */
                .fk-pill {
                    display: inline-flex; align-items: center; gap: 6px;
                    padding: 4px 12px; border-radius: 100px;
                    font-size: 13px; font-weight: 600; white-space: nowrap;
                }
                .fk-pill-gelir { background: #f0fdf4; color: #16a34a; }
                .fk-pill-gider { background: #fef2f2; color: #dc2626; }
                .fk-pill-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }

                .fk-cell-actions { width: 90px; text-align: right; }
                .fk-actions { display: flex; justify-content: flex-end; gap: 6px; }
                .fk-ghost-btn {
                    display: inline-flex; align-items: center; justify-content: center;
                    width: 32px; height: 32px; border-radius: 6px;
                    border: 1px solid #e5e7eb; background: #fff;
                    cursor: pointer; transition: all 0.15s; color: #64748b;
                }
                .fk-ghost-btn:hover { border-color: #cd6906; }
                .fk-ghost-edit:hover { color: #cd6906; }
                .fk-ghost-delete:hover { color: #e11d48; }
                .fk-ghost-btn svg { width: 15px; height: 15px; stroke: currentColor; stroke-width: 2; fill: none; stroke-linecap: round; stroke-linejoin: round; }

                /* Footer */
                .fk-footer { padding: 12px 20px; border-top: 1px solid #e5e7eb; display: flex; align-items: center; justify-content: space-between; }
                .fk-count { font-size: 14px; color: #6b7280; font-weight: 500; }
                .fk-count span { color: #475569; font-weight: 600; }

                /* Bulk action bar */
                .fk-bulk-bar {
                    position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
                    background: #1a1a1a; color: #fff; border-radius: 12px;
                    padding: 12px 24px; display: flex; align-items: center; gap: 16px;
                    box-shadow: 0 8px 32px rgba(0,0,0,0.25); z-index: 1040;
                    animation: fkBulkIn 0.2s ease;
                }
                @keyframes fkBulkIn { from { opacity: 0; transform: translateX(-50%) translateY(12px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
                .fk-bulk-bar span { font-size: 14px; font-weight: 600; }
                .fk-bulk-del {
                    padding: 8px 18px; border-radius: 8px; border: none;
                    background: #dc2626; color: #fff; font-size: 13px; font-weight: 600;
                    font-family: inherit; cursor: pointer; transition: background 0.15s;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .fk-bulk-del:hover { background: #b91c1c; }
                .fk-bulk-cancel {
                    padding: 8px 18px; border-radius: 8px;
                    border: 1px solid rgba(255,255,255,0.2); background: transparent;
                    color: #fff; font-size: 13px; font-weight: 500;
                    font-family: inherit; cursor: pointer; transition: background 0.15s;
                }
                .fk-bulk-cancel:hover { background: rgba(255,255,255,0.1); }
                .fk-checkbox {
                    width: 16px; height: 16px; border-radius: 4px;
                    border: 1.5px solid #d1d5db; cursor: pointer;
                    accent-color: #cd6906;
                }

                /* Empty */
                .fk-empty { text-align: center; padding: 56px 20px; color: #94a3b8; }
                .fk-empty-text { font-size: 15px; font-weight: 500; }

                /* ── Modal ── */
                .fk
                .fk-modal {
                    background: #fff; border-radius: 16px 0 0 16px;
                    width: 480px; max-width: 92vw;
                    height: 100%;
                    box-shadow: -8px 0 40px rgba(0,0,0,0.12);
                    animation: fkDrawerIn 0.28s cubic-bezier(0.32,0.72,0,1);
                    display: flex; flex-direction: column;
                    overflow: hidden;
                }
                @keyframes fkDrawerIn {
                    from { transform: translateX(100%); }
                    to   { transform: translateX(0); }
                } to{opacity:1;transform:translateY(0) scale(1)} }
                .fk-modal-header {
                    padding: 20px 24px; border-bottom: 1px solid #f3f4f6;
                    flex-shrink: 0;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .fk-modal-title { font-size: 18px; font-weight: 600; color: #0f172a; margin: 0; }
                .fk-modal-close {
                    width: 32px; height: 32px; border-radius: 8px; border: none;
                    background: none; color: #9ca3af; cursor: pointer;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 16px; transition: background 0.15s;
                }
                .fk-modal-close:hover { background: #f3f4f6; color: #374151; }
                .fk-modal-body { padding: 24px; display: flex; flex-direction: column; gap: 20px; flex: 1; overflow-y: auto; }
                .fk-field label {
                    display: block; font-size: 12px; font-weight: 600;
                    letter-spacing: 0.5px;
                    color: #64748b; margin-bottom: 6px;
                }
                .fk-field input, .fk-field select {
                    width: 100%; padding: 10px 14px;
                    border: 1.5px solid #e2e8f0; border-radius: 9px;
                    font-size: 14px; font-family: inherit; color: #0f172a;
                    outline: none; transition: border-color 0.15s, box-shadow 0.15s;
                    box-sizing: border-box; background: #fff;
                }
                .fk-field input:focus, .fk-field select:focus { border-color: #cd6906; box-shadow: 0 0 0 3px rgba(205,105,6,0.12); }
                .fk-tur-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
                .fk-tur-btn {
                    padding: 12px; border-radius: 9px; border: 1.5px solid #e2e8f0;
                    background: #f8fafc; cursor: pointer; font-size: 13px; font-weight: 600;
                    color: #64748b; font-family: inherit; transition: all 0.15s;
                    text-align: center;
                }
                .fk-tur-btn-gider-active { background: rgba(239,68,68,0.1); border-color: #ef4444; color: #dc2626; }
                .fk-tur-btn-gelir-active { background: rgba(16,185,129,0.1); border-color: #10b981; color: #059669; }
                .fk-modal-footer {
                    padding: 16px 24px; border-top: 1px solid #f3f4f6;
                    background: rgba(255,255,255,0.9); backdrop-filter: blur(8px); flex-shrink: 0;
                    display: flex; justify-content: flex-end; gap: 10px;
                }
                .fk-btn-cancel {
                    padding: 9px 18px; border-radius: 8px; border: none;
                    background: none; color: #4b5563;
                    font-size: 13px; font-weight: 500; font-family: inherit;
                    cursor: pointer; transition: background 0.15s;
                }
                .fk-btn-cancel:hover { background: #f9fafb; }
                .fk-btn-save {
                    padding: 10px 20px; border-radius: 8px; border: none;
                    background: #cd6906; color: #fff;
                    font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transition: background 0.15s, transform 0.1s;
                }
                .fk-btn-save:hover { background: #b85d05; transform: translateY(-1px); }

                /* ── Responsive ── */
                @media (max-width: 991px) {
                    .fk-page { padding: 18px 14px; }
                    .fk-card { overflow-x: auto; -webkit-overflow-scrolling: touch; }
                    .fk-table { min-width: 500px; }
                    .fk-footer { flex-wrap: wrap; gap: 8px; }
                }

                @media (max-width: 576px) {
                    .fk-page { padding: 14px 8px; }
                    .fk-stats { flex-direction: column; }
                    .fk-stat { min-width: 100%; }
                    .fk-title { font-size: 18px; }
                    .fk-table thead th { padding: 10px 12px; }
                    .fk-table tbody td { padding: 12px; font-size: 13px; }
                }
            `}</style>

            <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", padding: '24px', background: '#fff' }}>
                {/* Header */}
                <div style={{ paddingBottom: 20, marginBottom: 24, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
                    <div>
                        <h1 style={{ fontSize: 20, fontWeight: 600, color: '#111827', margin: 0, lineHeight: 1.3 }}>Gelir/Gider Kategorileri</h1>
                        <p style={{ fontSize: 13, color: '#6b7280', margin: '3px 0 0' }}>Finansal kategorileri yönetin</p>
                    </div>
                    <button
                        onClick={handleShowModal}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', boxShadow: '0 1px 3px rgba(205,105,6,0.3)', transition: 'all 0.15s', whiteSpace: 'nowrap' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#a85505'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#cd6906'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; }}
                    >
                        <svg style={{ width: 14, height: 14, stroke: '#fff', strokeWidth: 2.5, fill: 'none' }} viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        Yeni Kategori
                    </button>
                </div>

                {/* Stat cards */}
                <div className="fk-stats">
                    <div className="fk-stat fk-stat-gelir">
                        <div className="fk-stat-icon" style={{ background: '#dcfce7' }}>
                            <svg viewBox="0 0 24 24" stroke="#16a34a"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
                        </div>
                        <div><div className="fk-stat-val">{gelirKategorileri.length}</div><div className="fk-stat-lbl">Gelir Kategorisi</div></div>
                    </div>
                    <div className="fk-stat fk-stat-gider">
                        <div className="fk-stat-icon" style={{ background: '#fee2e2' }}>
                            <svg viewBox="0 0 24 24" stroke="#dc2626"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>
                        </div>
                        <div><div className="fk-stat-val">{giderKategorileri.length}</div><div className="fk-stat-lbl">Gider Kategorisi</div></div>
                    </div>
                </div>

                {/* Main Card */}
                <div className="fk-card">
                    {/* Tabs */}
                    <div className="fk-tabs">
                        <div className="fk-tab-bar">
                            <button className={`fk-tab${activeTab === 1 ? ' fk-tab-active' : ''}`} onClick={() => { setActiveTab(1); setSelectedIds([]); }}>
                                Gider ({giderKategorileri.length})
                            </button>
                            <button className={`fk-tab${activeTab === 0 ? ' fk-tab-active' : ''}`} onClick={() => { setActiveTab(0); setSelectedIds([]); }}>
                                Gelir ({gelirKategorileri.length})
                            </button>
                        </div>
                    </div>

                    {/* Table */}
                    <div style={{ padding: '16px 0' }}>
                        {currentKategoriler.length === 0 ? (
                            <div className="fk-empty">
                                <div className="fk-empty-text">{t('msg.henuzKayitEklenmedi')}</div>
                            </div>
                        ) : (
                            <table className="fk-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 40 }}><input type="checkbox" className="fk-checkbox" checked={selectedIds.length === currentKategoriler.length && currentKategoriler.length > 0} onChange={toggleSelectAll} /></th>
                                        <th style={{ width: 54 }}>#</th>
                                        <th>{t('ayarlar.finansKategori.adi')}</th>
                                        <th>{t('label.tur')}</th>
                                        <th className="fk-th-r">{t('label.islem')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentKategoriler.map((kategori) => (
                                        <tr key={kategori.kategoriID}>
                                            <td style={{ width: 40 }}><input type="checkbox" className="fk-checkbox" checked={selectedIds.includes(kategori.kategoriID)} onChange={() => toggleSelect(kategori.kategoriID)} /></td>
                                            <td className="fk-cell-id">#{kategori.kategoriID}</td>
                                            <td className="fk-cell-name">{kategori.kategoriAdi}</td>
                                            <td>
                                                <span className={`fk-pill ${kategori.tur === 'Gelir' ? 'fk-pill-gelir' : 'fk-pill-gider'}`}>
                                                    <span className="fk-pill-dot" style={{ background: kategori.tur === 'Gelir' ? '#10b981' : '#ef4444' }} />
                                                    {kategori.tur}
                                                </span>
                                            </td>
                                            <td className="fk-cell-actions">
                                                <div className="fk-actions">
                                                    <button className="fk-ghost-btn fk-ghost-edit" title={t('btn.duzenle')} onClick={() => handleEdit(kategori)}>
                                                        <svg viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                                    </button>
                                                    <button className="fk-ghost-btn fk-ghost-delete" title={t('btn.sil')} onClick={() => handleDelete(kategori.kategoriID)}>
                                                        <svg viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {currentKategoriler.length > 0 && (
                        <div className="fk-footer">
                            <span className="fk-count">Toplam <span>{currentKategoriler.length}</span> kategori</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Bulk action bar */}
            {selectedIds.length > 0 && (
                <div className="fk-bulk-bar">
                    <span>{selectedIds.length} {t('msg.secilenKayit')}</span>
                    <button className="fk-bulk-del" onClick={handleBulkDelete}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" width="14" height="14"><path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /></svg>
                        Sil
                    </button>
                    <button className="fk-bulk-cancel" onClick={() => setSelectedIds([])}>{t('btn.iptal')}</button>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fk-overlay" onClick={handleCloseModal}>
                    <div className="fk-modal" onClick={e => e.stopPropagation()}>
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                            <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', margin: 0 }}>{selectedKategori ? 'Kategori Düzenle' : 'Yeni Kategori Ekle'}</h2>
                            <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: 8, color: '#9ca3af', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32 }} onClick={handleCloseModal}>✕</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="fk-modal-body">
                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Kategori Adı</label>
                                    <input
                                        type="text"
                                        value={formData.kategoriAdi}
                                        onChange={e => setFormData({ ...formData, kategoriAdi: e.target.value })}
                                        required
                                        maxLength={100}
                                        autoFocus
                                        placeholder={t('label.ara')}
                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: '#111827', outline: 'none', boxSizing: 'border-box' as const, transition: 'border-color 0.15s, box-shadow 0.15s' }}
                                        onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                    />
                                </div>
                                <div className="fk-field">
                                    <label>Tür</label>
                                    <div className="fk-tur-grid">
                                        <button
                                            type="button"
                                            className={`fk-tur-btn${formData.tur === 1 ? ' fk-tur-btn-gider-active' : ''}`}
                                            onClick={() => setFormData({ ...formData, tur: 1 })}
                                        >
                                            Gider
                                        </button>
                                        <button
                                            type="button"
                                            className={`fk-tur-btn${formData.tur === 0 ? ' fk-tur-btn-gelir-active' : ''}`}
                                            onClick={() => setFormData({ ...formData, tur: 0 })}
                                        >
                                            Gelir
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div style={{ padding: '16px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: 10, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)', flexShrink: 0 }}>
                                <button
                                    type="button"
                                    style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                                    onClick={handleCloseModal}
                                >{t('btn.iptal')}</button>
                                <button
                                    type="submit"
                                    style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: '#cd6906', color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, boxShadow: '0 1px 3px rgba(205,105,6,0.3)' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#a85505'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; }}
                                >{selectedKategori ? 'Güncelle' : 'Kaydet'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default FinansKategorileriPage;
